<script setup>
  import { ref, computed, defineEmits, watch } from 'vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import TodoItemBlock from './TodoItemBlock.vue'
  import draggable from 'vuedraggable' // Vue3用のvuedraggable

  const props = defineProps({
    demo: {
      type: Boolean,
      default: false,
    },
    initialData: {
      type: Array,
      default: () => [],
    },
  })

  // イベント定義
  const emit = defineEmits(['save'])

  // ダイアログストアを初期化
  const dialogStore = useDialogStore()

  // メソッドを外部に公開
  defineExpose({
    revertToInitial,
    updateBackup,
    triggerSave, // 新しく追加
  })

  // データモデル
  const items = ref([])
  // バックアップ用の初期データを保持
  const initialBackup = ref([])
  // 現在のタブ（editor または preview）
  const activeTab = ref('editor')

  // 初期データが変更されたときに適用する
  watch(
    () => props.initialData,
    newData => {
      if (newData && newData.length > 0) {
        applyInitialData(newData)
      }
    },
    { immediate: true }
  )

  // 初期データを適用する関数
  function applyInitialData(data) {
    if (!data || data.length === 0) return

    // ディープコピーを作成
    const processedData = JSON.parse(JSON.stringify(data))

    // 各項目にid、collapsedプロパティを追加
    function processItems(items) {
      if (!items) return items

      if (Array.isArray(items)) {
        return items.map(item => {
          // idがなければ追加
          if (!item.id) {
            item.id = generateId()
          }

          // collapsedがなければデフォルト値を設定
          if (item.collapsed === undefined) {
            item.collapsed = false
          }

          // 選択肢がある場合は再帰的に処理
          if (item.selection) {
            item.selection = processItems(item.selection)
          }

          // 子項目がある場合は再帰的に処理
          if (item.children) {
            item.children = processItems(item.children)
          }

          // 選択肢の場合（optionプロパティがある場合）
          if (item.option) {
            if (item.collapsed === undefined) {
              item.collapsed = false
            }

            if (item.children) {
              item.children = processItems(item.children)
            }
          }

          return item
        })
      }

      return items
    }

    // データを処理
    const result = processItems(processedData)

    // 初期バックアップを保存
    initialBackup.value = JSON.parse(JSON.stringify(result))

    // 項目に設定
    items.value = result
  }

  // JSONプレビューを計算
  const jsonOutput = computed(() => {
    const cleanData = JSON.parse(JSON.stringify(items.value))
    return JSON.stringify(removeKey(cleanData), null, 2)
  })

  // 余分なキーを削除
  function removeKey(data) {
    if (Array.isArray(data)) {
      return data.map(item => removeKey(item))
    } else if (data && typeof data === 'object') {
      const newObj = {}
      for (const key in data) {
        if (key !== 'collapsed' && key !== 'id') {
          newObj[key] = removeKey(data[key])
        }
      }
      return newObj
    }
    return data
  }

  // タブを切り替える
  function switchTab(tab) {
    activeTab.value = tab
  }

  // 新しいアイテムを追加
  function addItem() {
    items.value.push({
      id: generateId(), // 一意のIDを追加
      label: '',
      tip: '',
      collapsed: false,
    })
  }

  // 一意のIDを生成
  function generateId() {
    return '_' + Math.random().toString(36).substr(2, 9)
  }

  // アイテムを削除 - ダイアログストアを使用する修正版
  async function deleteItem(index, event) {
    // すでに確認済みの場合は処理をスキップ
    if (event && event.confirmed) {
      items.value.splice(index, 1)
      return
    }

    // ダイアログストアを使用して確認
    const confirmed = await dialogStore.showConfirmDialog('削除確認', 'この項目を削除してもよろしいですか？')
    if (confirmed) {
      items.value.splice(index, 1)
    }
  }

  // 全て削除 - ダイアログストアを使用する修正版
  async function deleteAll() {
    if (items.value.length === 0) {
      return // 削除する項目がない場合は何もしない
    }

    // ダイアログストアを使用して確認
    const confirmed = await dialogStore.showConfirmDialog('全削除確認', 'すべての項目を削除してもよろしいですか？')
    if (confirmed) {
      items.value = []
    }
  }

  // 全て展開
  function expandAll() {
    function expand(items) {
      if (!items) return

      if (Array.isArray(items)) {
        items.forEach(item => {
          item.collapsed = false
          if (item.selection) expand(item.selection)
          if (item.children) expand(item.children)
        })
      }
    }
    expand(items.value)
  }

  // 全て折りたたむ
  function collapseAll() {
    function collapse(items) {
      if (!items) return

      if (Array.isArray(items)) {
        items.forEach(item => {
          item.collapsed = true
          if (item.selection) collapse(item.selection)
          if (item.children) collapse(item.children)
        })
      }
    }
    collapse(items.value)
  }

  // サンプルデータを読み込む
  function loadSampleData() {
    // サンプルデータの各項目に一意のIDを追加する関数
    function addIds(items) {
      if (!items) return

      if (Array.isArray(items)) {
        items.forEach(item => {
          if (!item.id) item.id = generateId()
          if (item.selection) addIds(item.selection)
          if (item.children) addIds(item.children)
        })
      }
      return items
    }

    const sampleData = [
      {
        label: '本人もしくは保健所より感染の連絡',
        tip: '',
        collapsed: false,
      },
      {
        label: '休業命令（保健所から指定された期間）',
        tip: '',
        collapsed: false,
      },
      {
        label: '本⼈による選択',
        tip: '',
        collapsed: false,
        selection: [
          {
            option: '有給休暇',
            collapsed: false,
            children: [
              {
                label: '有給休暇',
                tip: '',
                collapsed: false,
                selection: [
                  {
                    option: 'あり',
                    collapsed: false,
                    children: [
                      {
                        label: '保健所指定期間経過',
                        tip: '',
                        collapsed: false,
                      },
                      {
                        label: '回復後勤務復帰',
                        tip: '',
                        collapsed: false,
                      },
                    ],
                  },
                  {
                    option: '有給が無くなった場合',
                    collapsed: false,
                    children: [
                      {
                        label: '傷病⼿当⾦申請',
                        tip: '',
                        collapsed: false,
                      },
                      {
                        label: '保健所指定期間経過',
                        tip: '',
                        collapsed: false,
                      },
                      {
                        label: '回復後勤務復帰',
                        tip: '',
                        collapsed: false,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            option: '傷病手当',
            collapsed: false,
            children: [
              {
                label: '傷病⼿当⾦申請',
                tip: '',
                collapsed: false,
              },
              {
                label: '保健所指定期間経過',
                tip: '',
                collapsed: false,
              },
              {
                label: '回復後勤務復帰',
                tip: '',
                collapsed: false,
              },
            ],
          },
        ],
      },
    ]

    // サンプルデータにIDを追加
    items.value = addIds(sampleData)
  }

  // クリップボードにコピー - ダイアログストアを使用する修正版
  async function copyToClipboard() {
    const cleanData = JSON.parse(JSON.stringify(items.value))
    const jsonStr = JSON.stringify(removeKey(cleanData), null, 2)

    try {
      await navigator.clipboard.writeText(jsonStr)
      // ダイアログストアを使用して成功メッセージを表示
      await dialogStore.showMessageDialog('コピー完了', 'JSONをクリップボードにコピーしました！')
    } catch (err) {
      // ダイアログストアを使用してエラーメッセージを表示
      await dialogStore.showMessageDialog('コピー失敗', 'コピーに失敗しました。手動でコピーしてください。')
    }
  }

  // データを保存（saveイベントを発行）
  function saveData() {
    const cleanData = JSON.parse(JSON.stringify(items.value))
    const cleanedData = removeKey(cleanData)
    emit('save', cleanedData)
  }

  // 外部から保存をトリガーする関数
  function triggerSave() {
    saveData()
  }

  // ドラッグ中の項目のスタイルをカスタマイズ
  const dragOptions = {
    animation: 150,
    ghostClass: 'ghost',
    chosenClass: 'chosen',
    dragClass: 'drag',
  }

  // 外部から呼び出せるように公開するメソッド
  // 初期状態に戻す
  function revertToInitial() {
    if (initialBackup.value.length > 0) {
      items.value = JSON.parse(JSON.stringify(initialBackup.value))
    }
  }

  // 現在の状態をバックアップとして保存
  function updateBackup() {
    initialBackup.value = JSON.parse(JSON.stringify(items.value))
  }
</script>

<template>
  <div class="todo-editor">
    <div class="container">
      <div class="tab-container">
        <!-- タブナビゲーション -->
        <div class="tabs">
          <!-- <button class="tab-btn" :class="{ active: activeTab === 'editor' }" @click="switchTab('editor')">
            <span class="material-symbols-outlined">edit_note</span>
            エディタ
          </button>
          <button class="tab-btn" :class="{ active: activeTab === 'preview' }" @click="switchTab('preview')">
            <span class="material-symbols-outlined">code</span>
            JSON プレビュー
          </button> -->
        </div>
        <!-- 共有ツールバー -->
        <div class="toolbar">
          <div v-if="activeTab === 'editor'" class="btn-group">
            <!-- <button class="btn btn-green" @click="saveData" title="データを保存">
              <span class="material-symbols-outlined">save</span>
              保存
            </button> -->
            <!-- <button class="btn" @click="loadSampleData" title="サンプルデータを読み込む">
              <span class="material-symbols-outlined">database</span>
              サンプル読込
            </button> -->
            <button type="button" class="btn btn-border" @click="expandAll" title="すべて展開">すべて展開</button>
            <button type="button" class="btn btn-border" @click="collapseAll" title="すべて折りたたむ">すべて折りたたむ</button>
            <button type="button" class="btn btn-red" @click="deleteAll" title="すべて削除">すべて削除</button>
          </div>
          <div v-if="activeTab === 'preview'" class="btn-group">
            <button class="btn btn-green" @click="saveData" title="データを保存">
              <span class="material-symbols-outlined">save</span>
              保存
            </button>
            <button class="btn btn-blue" @click="copyToClipboard" title="JSONをコピー">
              <span class="material-symbols-outlined">content_copy</span>
              コピー
            </button>
          </div>
        </div>
      </div>

      <!-- タブコンテンツ -->
      <div class="tab-content">
        <!-- エディタタブ -->
        <div v-if="activeTab === 'editor'" class="editor">
          <!-- ルートレベルのアイテム (ドラッグ可能) -->
          <draggable v-model="items" item-key="id" v-bind="dragOptions" handle=".drag-handle" class="drag-container">
            <template #item="{ element, index }">
              <TodoItemBlock :item="element" :is-option="false" :depth="1" @delete="event => deleteItem(index, event)" />
            </template>
          </draggable>

          <!-- 下部に移動した新規項目追加ボタン -->
          <div class="footer-actions">
            <button class="btn btn-enabled" @click="addItem">項目を追加</button>
          </div>
        </div>

        <!-- プレビュータブ -->
        <div v-if="activeTab === 'preview'" class="preview">
          <pre>{{ jsonOutput }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .todo-editor {
    height: 100%; /* ビューポート全体の高さを使用 */
    position: relative;
  }
  .container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: white;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    height: 100%; /* ビューポートの高さの90%を使用 */
    display: grid;
    grid-template-rows: auto 1fr; /* ヘッダーとコンテンツの比率を指定 */
  }
  .tab-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: white;
    padding: 10px 15px;
  }
  .tabs {
    display: flex;
    gap: 2px;
  }
  .tab-btn {
    background: none;
    border: none;
    padding: 10px 15px;
    font-size: 15px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #666;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;

    &:hover {
      color: #333;
      background-color: rgba(0, 0, 0, 0.03);
    }

    &.active {
      color: #2196f3;
      border-bottom: 2px solid #2196f3;
      font-weight: 500;
    }

    .material-symbols-outlined {
      font-size: 20px;
    }
  }
  .tab-content {
    padding: 0 10px;
    flex-grow: 1; /* コンテナ内で残りのスペースを埋める */
    display: flex;
    flex-direction: column;
    overflow: hidden; /* オーバーフローを防ぐ */
  }
  .editor,
  .preview {
    overflow-y: auto;
    flex-grow: 1; /* タブコンテンツ内で残りのスペースを埋める */
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    padding-bottom: 20px;
  }
  .toolbar {
    .btn-group {
      display: flex;
      gap: 5px;
    }
  }
  .footer-actions {
    text-align: center;
  }
  .btn {
    color: #fff;
    border: none;
    border-radius: 11px;
    font-size: 12px;
    font-weight: bold;
    min-width: 68px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.3s;
    white-space: nowrap;
    padding: 0px 8px;

    &-sm {
      padding: 4px 8px;
      font-size: 12px;
    }
    &-border {
      color: var(--active-color);
      border: 1px solid var(--active-color);
    }
    &-red {
      background-color: var(--alert-color);
    }
    &-enabled {
      background-color: var(--sub-color);
    }
  }
  pre {
    background-color: #f5f5f5;
    padding: 15px;
    border-radius: 4px;
    overflow: auto;
    white-space: pre-wrap;
    font-size: 14px;
    margin: 0;
    flex-grow: 1; /* プレビュー内で残りのスペースを埋める */
  }
  .material-symbols-outlined {
    font-size: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ドラッグ&ドロップのスタイル */
  .drag-container {
    width: 100%;
  }
  .ghost {
    opacity: 0.5;
    background: #c8ebfb;
    border: 1px dashed #2196f3;
  }
  .chosen {
    background-color: #f0f8ff;
  }
  .drag {
    background-color: #f5f5f5;
  }
</style>
